import type { Transition, Variants } from "motion/react";

/**
 * 動畫預設值
 * ---------------------------------------------------------------
 * 全站動畫都從這裡取，避免每個元件各自調參數導致節奏不一致。
 *
 * 原則：
 *  - 進場一律「淡入 + 小幅上移」，位移不超過 24px，超過會顯得廉價。
 *  - 用 spring 而不是 ease，手感比較像實體物件。
 *  - 所有動畫都必須在 prefers-reduced-motion 下自動退化（見 globals.css）。
 */

export const spring: Transition = {
  type: "spring",
  stiffness: 260,
  damping: 30,
  mass: 0.9,
};

export const springSnappy: Transition = {
  type: "spring",
  stiffness: 420,
  damping: 34,
  mass: 0.7,
};

/** 單一元素進場 */
export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: spring },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4, ease: "easeOut" } },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1, transition: spring },
};

/** 容器：讓子項依序進場。搭配 fadeInUp 當子項 variants。 */
export function stagger(delayChildren = 0, staggerChildren = 0.06): Variants {
  return {
    hidden: {},
    visible: {
      transition: { delayChildren, staggerChildren },
    },
  };
}

/** 頁面切換：淡入 + 極小位移，避免像投影片翻頁 */
export const pageTransition: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.18, ease: "easeIn" } },
};

/** 卡片 hover：抬升一點點就好 */
export const cardHover = {
  rest: { y: 0 },
  hover: { y: -4, transition: springSnappy },
};

/** 進入視窗才播放的預設值：只播一次，元素露出 20% 時觸發 */
export const viewportOnce = { once: true, amount: 0.2 } as const;
